import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { toast } from 'react-toast';
import { LuSearch, LuChevronDown, LuX } from 'react-icons/lu';
import AITestCaseGenerationModal from './AITestCaseGenerationModal';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { Pagination } from '../ui';

const normalizeProject = (project) => {
  if (!project) return null;
  const id = project.project_id ?? project.id;
  if (id == null) return null;
  return {
    ...project,
    id,
    project_name:
      project.project_name ||
      project.name ||
      project.projectName ||
      `Project ${id}`,
  };
};

/**
 * Test Case Optimization & Coverage Analysis component
 * Provides tools for redundancy detection and test coverage analysis
 */
const TestCaseOptimization = () => {
  const { selectedProject: contextProject, projects: contextProjects } = useProject();
  const { getProjectIdFromURL } = useWorkspaceRouting();
  const urlProjectId = getProjectIdFromURL();

  const [testCases, setTestCases] = useState([]);
  const [userStories, setUserStories] = useState([]);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [similarityThreshold, setSimilarityThreshold] = useState(80);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('redundancy');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [mappingView, setMappingView] = useState('matrix'); // 'matrix' or 'uncovered'
  const [currentPage, setCurrentPage] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'compact'
  const [filteredTestCases, setFilteredTestCases] = useState([]);
  const [testCasesPerPage] = useState(5);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [isAISuggestionModalOpen, setIsAISuggestionModalOpen] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const projectPickerRef = useRef(null);
  const projectSearchRef = useRef(null);
  const autoSelectedRef = useRef(false);
  const contextProjectRef = useRef(contextProject);
  const contextProjectsRef = useRef(contextProjects);
  contextProjectRef.current = contextProject;
  contextProjectsRef.current = contextProjects;

 const [coverageData, setCoverageData] = useState({
  overallCoverage: 0,
  requirementsCovered: { covered: 0, total: 0 },
  gapsIdentified: 0,
  moduleCoverage: []
});
const [coverageLoading, setCoverageLoading] = useState(false);
 const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  
  
const getAuthToken = () => {
  const authDataString = localStorage.getItem("user");
  const headers = {
    "Content-Type": "application/json",
  };
  
  if (authDataString) {
    try {
      const authData = JSON.parse(authDataString);
      if (authData.accessToken) {
        headers["token"] = authData.accessToken;
      }
    } catch (error) {
      console.error("Error parsing auth data:", error);
    }
  }
  return headers;
};

const mergeProjects = (lists) => {
  const seen = new Set();
  const out = [];
  lists.flat().forEach((raw) => {
    const p = normalizeProject(raw);
    if (!p || seen.has(String(p.id))) return;
    seen.add(String(p.id));
    out.push(p);
  });
  return out;
};

/** Load all accessible projects (not workspace-scoped). */
const fetchProjects = useCallback(async () => {
  setProjectsLoading(true);
  setProjectsError(null);

  try {
    const pageSize = 100;
    let page = 1;
    let totalPages = 1;
    const all = [];
    const seen = new Set();

    do {
      const params = {
        page,
        page_size: pageSize,
        pro_status: 'active',
      };

      const response = await axiosInstance.get('/projects/list_project', { params });
      const batch = response?.projects || response?.items || response?.data?.projects || [];
      const totalCount = Number(response?.total_count ?? response?.count ?? 0);
      const totalPagesFromApi = Number(response?.total_pages ?? 0);
      totalPages =
        totalPagesFromApi > 0
          ? totalPagesFromApi
          : totalCount > 0
            ? Math.ceil(totalCount / pageSize)
            : batch.length < pageSize
              ? page
              : page + 1;

      batch.forEach((raw) => {
        const p = normalizeProject(raw);
        if (!p || seen.has(String(p.id))) return;
        seen.add(String(p.id));
        all.push(p);
      });

      if (batch.length === 0 || page >= 50) break;
      page += 1;
    } while (page <= totalPages);

    // Keep the current URL / context project visible even if paging missed it.
    const contextCandidates = [
      contextProjectRef.current,
      ...(Array.isArray(contextProjectsRef.current) ? contextProjectsRef.current : []),
    ]
      .map(normalizeProject)
      .filter(Boolean);

    const merged = mergeProjects([all, contextCandidates]);
    setProjects(merged);
    return merged;
  } catch (error) {
    console.error('Error fetching projects:', error);
    setProjectsError(error?.message || 'Failed to load projects');
    const fallback = mergeProjects([
      [contextProjectRef.current],
      Array.isArray(contextProjectsRef.current) ? contextProjectsRef.current : [],
    ]);
    setProjects(fallback);
    return fallback;
  } finally {
    setProjectsLoading(false);
  }
}, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // If context/top-bar projects arrive later, merge them in without a full reload.
  useEffect(() => {
    const extras = [
      contextProject,
      ...(Array.isArray(contextProjects) ? contextProjects : []),
    ]
      .map(normalizeProject)
      .filter(Boolean);
    if (!extras.length) return;
    setProjects((prev) => mergeProjects([prev, extras]));
  }, [contextProject, contextProjects]);

  // Auto-select the project already open in the top bar / URL.
  useEffect(() => {
    if (autoSelectedRef.current || selectedProject) return;
    const targetId = urlProjectId || contextProject?.id || contextProject?.project_id;
    if (!targetId) return;
    const match = projects.find((p) => String(p.id) === String(targetId));
    if (match) {
      setSelectedProject(match);
      autoSelectedRef.current = true;
    } else if (contextProject) {
      const normalized = normalizeProject(contextProject);
      if (normalized && String(normalized.id) === String(targetId)) {
        setSelectedProject(normalized);
        autoSelectedRef.current = true;
        setProjects((prev) => mergeProjects([prev, [normalized]]));
      }
    }
  }, [projects, selectedProject, urlProjectId, contextProject]);

  // Load test cases and user stories when project changes
  useEffect(() => {
    if (!selectedProject) return;
    
    // In a real app, this would be fetched from an API with the project ID
    
    // Mock requirements data
    const mockRequirements = [
      // E-commerce Platform (Project 1)
      { id: 1, projectId: 1, title: 'User Authentication', description: 'Users should be able to register, login, and reset passwords', covered: true },
      { id: 2, projectId: 1, title: 'Product Search', description: 'Users should be able to search for products by name, category, and attributes', covered: true },
      { id: 3, projectId: 1, title: 'Shopping Cart', description: 'Users should be able to add products to cart and manage quantities', covered: true },
      { id: 4, projectId: 1, title: 'Checkout Process', description: 'Users should be able to complete purchases with various payment methods', covered: true },
      { id: 5, projectId: 1, title: 'Order Tracking', description: 'Users should be able to track their order status', covered: false },
      
      // CRM System (Project 2)
      { id: 6, projectId: 2, title: 'Customer Management', description: 'System should allow creating and managing customer profiles', covered: true },
      { id: 7, projectId: 2, title: 'Lead Tracking', description: 'System should track leads through the sales pipeline', covered: true },
      { id: 8, projectId: 2, title: 'Email Integration', description: 'System should integrate with email services for communication', covered: false },
      { id: 9, projectId: 2, title: 'Reporting', description: 'System should generate sales and activity reports', covered: true },
      
      // Banking Portal (Project 3)
      { id: 10, projectId: 3, title: 'Account Management', description: 'Users should be able to view and manage their accounts', covered: true },
      { id: 11, projectId: 3, title: 'Fund Transfers', description: 'Users should be able to transfer funds between accounts', covered: true },
      { id: 12, projectId: 3, title: 'Bill Payments', description: 'Users should be able to pay bills from their accounts', covered: true },
      { id: 13, projectId: 3, title: 'Mobile Check Deposit', description: 'Users should be able to deposit checks using mobile camera', covered: false },
    ];
    
    // Get project ID
    const projectId = selectedProject?.id;
    
    // Filter requirements by project
    const filteredRequirements = mockRequirements.filter(req => req.projectId === projectId);
    setRequirements(filteredRequirements);
    // Filter test cases by project
    
    // Mock test cases with project IDs
    const allTestCases = [
      // E-commerce Platform (Project 1)
      { id: 1, projectId: 1, title: 'Login with valid credentials', steps: 'Enter username, Enter password, Click login', expected: 'User is logged in', priority: 'High', status: 'Passed' },
      { id: 2, projectId: 1, title: 'Login with invalid credentials', steps: 'Enter invalid username, Enter password, Click login', expected: 'Error message is displayed', priority: 'High', status: 'Passed' },
      { id: 3, projectId: 1, title: 'Login with valid user credentials', steps: 'Enter valid username, Enter password, Click login button', expected: 'User should be logged in successfully', priority: 'High', status: 'Passed' },
      { id: 4, projectId: 1, title: 'Search for product', steps: 'Enter product name, Click search', expected: 'Product results are displayed', priority: 'Medium', status: 'Passed' },
      { id: 5, projectId: 1, title: 'Search product by name', steps: 'Enter product name in search box, Click search button', expected: 'Matching products should be displayed', priority: 'Medium', status: 'Failed' },
      
      // CRM System (Project 2)
      { id: 6, projectId: 2, title: 'Create new customer', steps: 'Fill customer details, Click save', expected: 'Customer is created', priority: 'High', status: 'Passed' },
      { id: 7, projectId: 2, title: 'Add new customer', steps: 'Enter customer information, Click create', expected: 'New customer should be added to the system', priority: 'High', status: 'Passed' },
      { id: 8, projectId: 2, title: 'Update customer details', steps: 'Modify customer information, Save changes', expected: 'Customer details are updated', priority: 'Medium', status: 'Failed' },
      
      // Banking Portal (Project 3)
      { id: 9, projectId: 3, title: 'Transfer funds between accounts', steps: 'Select accounts, Enter amount, Confirm transfer', expected: 'Funds are transferred', priority: 'Critical', status: 'Passed' },
      { id: 10, projectId: 3, title: 'Check account balance', steps: 'Navigate to accounts page, Select account', expected: 'Balance is displayed', priority: 'High', status: 'Passed' }
    ];
    
    // Mock user stories with project IDs
    const allUserStories = [
      // E-commerce Platform (Project 1)
      { id: 1, projectId: 1, title: 'User Authentication', description: 'As a user, I want to log in to the system so that I can access my account', testCases: [1, 2, 3] },
      { id: 2, projectId: 1, title: 'Product Search', description: 'As a user, I want to search for products so that I can find what I need quickly', testCases: [4, 5] },
      
      // CRM System (Project 2)
      { id: 3, projectId: 2, title: 'Customer Management', description: 'As a sales rep, I want to manage customer information', testCases: [6, 7, 8] },
      
      // Banking Portal (Project 3)
      { id: 4, projectId: 3, title: 'Account Operations', description: 'As a bank customer, I want to perform operations on my accounts', testCases: [9, 10] }
    ];
    
    // Filter by selected project
    const filteredTestCases = allTestCases.filter(tc => tc.projectId === projectId);
    const filteredUserStories = allUserStories.filter(us => us.projectId === projectId);
    
    setTestCases(filteredTestCases);
    setUserStories(filteredUserStories);
    
    // Generate duplicate groups based on the filtered test cases
    const duplicates = [];
    
    if (projectId === 1) {
      duplicates.push({ 
        similarity: 92, 
        testCases: [
          { id: 1, title: 'Login with valid credentials' },
          { id: 3, title: 'Login with valid user credentials' }
        ]
      },
      { 
        similarity: 85, 
        testCases: [
          { id: 4, title: 'Search for product' },
          { id: 5, title: 'Search product by name' }
        ]
      });
    } else if (projectId === 2) {
      duplicates.push({ 
        similarity: 90, 
        testCases: [
          { id: 6, title: 'Create new customer' },
          { id: 7, title: 'Add new customer' }
        ]
      });
    }
    
    setDuplicateGroups(duplicates);
    setFilteredTestCases(filteredTestCases);
  }, [selectedProject]);

  const handleMergeDuplicates = (groupId) => {
    // In a real app, this would call an API to merge the test cases
    toast.success(`Merged test cases in group ${groupId + 1}`);
  };

const handleAnalyze = async () => {
  setLoading(true);
  
  try {
    const authHeaders = getAuthToken();
    
    const response = await fetch(
      `${API_BASE_URL}/test_case/duplicates?project_id=${selectedProject.id}&threshold=${similarityThreshold}`,
      {
        method: 'GET',
        headers: authHeaders
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
   
    const transformedGroups = data.duplicate_groups.map(group => ({
      similarity: group.average_similarity,
      testCases: group.test_cases.map(tc => ({
        id: tc.test_case_id,
        title: tc.name,
        similarity: tc.similarity_with_group
      }))
    }));
    
    setDuplicateGroups(transformedGroups);
    
  } catch (error) {
    console.error('Error fetching duplicate test cases:', error);
    toast.warning('Failed to analyze duplicates. Using mock data instead.');
    
  
    if (selectedProject.id === 1) {
      setDuplicateGroups([
        { 
          similarity: 92, 
          testCases: [
            { id: 1, title: 'Login with valid credentials', similarity: 100 },
            { id: 3, title: 'Login with valid user credentials', similarity: 92 }
          ]
        },
        { 
          similarity: 85, 
          testCases: [
            { id: 4, title: 'Search for product', similarity: 100 },
            { id: 5, title: 'Search product by name', similarity: 85 }
          ]
        }
      ]);
    } else if (selectedProject.id === 2) {
      setDuplicateGroups([
        { 
          similarity: 90, 
          testCases: [
            { id: 6, title: 'Create new customer', similarity: 100 },
            { id: 7, title: 'Add new customer', similarity: 90 }
          ]
        }
      ]);
    } else {
      setDuplicateGroups([]);
    }
  } finally {
    setLoading(false);
  }
};

const fetchCoverageData = async () => {
  if (!selectedProject) return;
  
  setCoverageLoading(true);
  try {
    const authHeaders = getAuthToken();
    
    const response = await fetch(
      `${API_BASE_URL}/test_cases/test_coverage?project_id=${selectedProject.id}`,
      {
        method: 'GET',
        headers: authHeaders
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    

    setCoverageData({
      overallCoverage: data.overall_coverage || 0,
      requirementsCovered: {
        covered: data.requirements_covered || 0,
        total: data.requirements_total || 0
      },
      gapsIdentified: data.gaps_identified || 0,
      moduleCoverage: data.module_coverage || []
    });
    
  } catch (error) {
    console.error('Error fetching coverage data:', error);
   
    setCoverageData({
      overallCoverage: selectedProject?.id === 1 ? 85 : selectedProject?.id === 2 ? 72 : 90,
      requirementsCovered: {
        covered: selectedProject?.id === 1 ? 24 : selectedProject?.id === 2 ? 15 : 18,
        total: selectedProject?.id === 1 ? 28 : selectedProject?.id === 2 ? 21 : 20
      },
      gapsIdentified: selectedProject?.id === 1 ? 12 : selectedProject?.id === 2 ? 8 : 5,
      moduleCoverage: selectedProject?.id === 1 ? [
        { module: 'Authentication', coverage: 92 },
        { module: 'User Management', coverage: 78 },
        { module: 'Product Catalog', coverage: 85 },
        { module: 'Shopping Cart', coverage: 64 },
        { module: 'Checkout', coverage: 72 }
      ] : selectedProject?.id === 2 ? [
        { module: 'Customer Management', coverage: 85 },
        { module: 'Lead Tracking', coverage: 65 },
        { module: 'Reporting', coverage: 70 },
        { module: 'Integration', coverage: 68 }
      ] : [
        { module: 'Account Management', coverage: 95 },
        { module: 'Transfers', coverage: 92 },
        { module: 'Bill Payments', coverage: 88 },
        { module: 'Security', coverage: 85 }
      ]
    });
  } finally {
    setCoverageLoading(false);
  }
};
useEffect(() => {
  if (selectedProject) {
    fetchCoverageData();
  }
}, [selectedProject]);
  // Handle AI test case generation
  const handleGenerateTestCases = (requirement, module) => {
    setSelectedRequirement({
      requirement: requirement.req,
      module: module,
      projectId: selectedProject.id
    });
    setIsAIModalOpen(true);
  };

  // Handle test cases generated by AI
  const handleTestCasesGenerated = (testCases) => {
    // In a real app, this would save the test cases to the database
    // and update the UI to show the new test cases
    toast.success(`${testCases.length} test cases generated successfully!`);
    
    // Close the modal
    setIsAIModalOpen(false);
  };

  // Handle edit test case
  const handleEditTestCase = (testCase) => {
    setSelectedTestCase(testCase);
    setIsEditModalOpen(true);
  };

  // Handle test case update
  const handleTestCaseUpdate = (updatedTestCase) => {
    // In a real app, this would update the test case in the database
    // For now, we'll update it in our local state
    const updatedTestCases = testCases.map(tc =>
      tc.id === updatedTestCase.id ? updatedTestCase : tc
    );
    setTestCases(updatedTestCases);
    setIsEditModalOpen(false);
  };

  // Handle AI suggestions for test case improvement
  const handleAISuggestions = (testCase) => {
    setSelectedTestCase(testCase);
    setIsAISuggestionModalOpen(true);
  };

  // Handle applying AI suggestions
  const handleApplySuggestions = (suggestions, testCaseId) => {
    // In a real app, this would update the test case with the suggestions
    toast.success(`Applied suggestions to test case ${testCaseId}`);
    setIsAISuggestionModalOpen(false);
  };

  const filteredProjects = useMemo(() => {
    const q = projectSearch.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!q) return projects;
    const tokens = q.split(' ').filter(Boolean);
    return projects.filter((p) => {
      const hay = `${p.project_name || ''} ${p.name || ''} ${p.description || ''} ${p.id || ''}`
        .toLowerCase()
        .replace(/\s+/g, ' ');
      return tokens.every((token) => hay.includes(token));
    });
  }, [projects, projectSearch]);

  const selectProject = useCallback((project) => {
    setSelectedProject(project);
    setProjectPickerOpen(false);
    setProjectSearch('');
  }, []);

  const clearSelectedProject = useCallback((e) => {
    e?.stopPropagation?.();
    setSelectedProject(null);
    setProjectSearch('');
  }, []);

  useEffect(() => {
    if (!projectPickerOpen) return undefined;
    const onDocClick = (e) => {
      if (projectPickerRef.current && !projectPickerRef.current.contains(e.target)) {
        setProjectPickerOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setProjectPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => projectSearchRef.current?.focus(), 40);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [projectPickerOpen]);

  // Handle filter change
  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
  };
  
  // Apply filter
  const applyFilter = () => {
    if (!filterText.trim()) {
      setFilteredTestCases(testCases);
    } else {
      const filtered = testCases.filter(tc =>
        tc.title.toLowerCase().includes(filterText.toLowerCase())
      );
      setFilteredTestCases(filtered);
    }
    setCurrentPage(0); // Reset to first page when filtering
  };


  const handleRefreshProjects = () => {
    fetchProjects();
  };

  // Render redundancy detection tab content
  const renderRedundancyTab = () => {
    return (
      <div>
        <div className="section">
          <div className="control-bar">
            <h3 className="section-title">Redundancy Detection</h3>
            <div className="threshold-control">
  <label className="threshold-label">Similarity Threshold:</label>
  <input
    type="range"
    min="50"
    max="100"
    value={similarityThreshold}
    onChange={(e) => setSimilarityThreshold(e.target.value)}
  />
  <span className="threshold-value">{similarityThreshold}%</span>
  <button
    className="analyze-button"
    onClick={handleAnalyze}
    disabled={loading}
  >
    {loading ? (
      <>
        <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
        Analyzing...
      </>
    ) : (
      'Analyze'
    )}
  </button>
</div>
          </div>
          
          <div className="content-panel">
            <p className="info-text">
              The system has identified the following potential duplicate test cases.
              Review and merge them to optimize your test suite.
            </p>
            
            {duplicateGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="duplicate-group">
                <div className="duplicate-header">
                  <div>
                    <span className="duplicate-title">Duplicate Group {groupIndex + 1}</span>
                    <span className="similarity-badge">Similarity: {group.similarity}%</span>
                  </div>
                  <button
                    className="merge-button"
                    onClick={() => handleMergeDuplicates(groupIndex)}
                  >
                    Merge
                  </button>
                </div>
               {group.testCases.map(testCase => (
  <div key={testCase.id} className="test-case-item">
    <div className="test-case-title">{testCase.title}</div>
    <div className="test-case-id">ID: TC-{testCase.id}</div>
    <div className="similarity-indicator">
      Similarity: {Math.round(testCase.similarity)}%
    </div>
  </div>
))}
              </div>
            ))}
            
            {duplicateGroups.length === 0 && (
              <div className="empty-state">
                No duplicate test cases found.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render coverage analysis tab content
  const renderCoverageTab = () => {
    return (
      <div>
        <div className="section">
          <h3 className="section-title">Test Coverage Analysis</h3>
          <div className="content-panel">
           <div className="stats-grid">
  <div className="stat-card">
    <div className="stat-value primary">
      {coverageLoading ? '...' : `${coverageData.overallCoverage}%`}
    </div>
    <div className="stat-label">Overall Coverage</div>
  </div>
  <div className="stat-card">
    <div className="stat-value success">
      {coverageLoading ? '...' : `${coverageData.requirementsCovered.covered}/${coverageData.requirementsCovered.total}`}
    </div>
    <div className="stat-label">Requirements Covered</div>
  </div>
  <div className="stat-card">
    <div className="stat-value warning">
      {coverageLoading ? '...' : coverageData.gapsIdentified}
    </div>
    <div className="stat-label">Gaps Identified</div>
  </div>
</div>
            
            <h4 className="subsection-title">Coverage by Suite</h4>
           <div className="progress-section">
  {coverageLoading ? (
    <div className="loading-indicator">
      <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
      Loading module coverage...
    </div>
  ) : (
    coverageData.moduleCoverage.map((module, index) => (
      <div key={index} className="progress-item">
        <div className="progress-header">
          <span className="progress-label">{module.module}</span>
          <span className="progress-value">{module.coverage}%</span>
        </div>
        <div className="progress-bar-bg">
          <div
            className={`progress-bar-fill ${module.coverage > 80 ? 'high' : module.coverage > 60 ? 'medium' : 'low'}`}
            style={{ width: `${module.coverage}%` }}
          ></div>
        </div>
      </div>
    ))
  )}
</div>
            
            <h4 className="subsection-title">Coverage Gaps</h4>
            <div className="table-container">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Requirement</th>
                    <th className="table-header-cell">Module</th>
                    <th className="table-header-cell">Priority</th>
                    <th className="table-header-cell">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Project 1: E-commerce Platform */}
                  {selectedProject?.id === 1 && [
                    { id: 1, req: 'Password reset functionality', module: 'Authentication', priority: 'High' },
                    { id: 2, req: 'Product filtering by multiple attributes', module: 'Product Catalog', priority: 'Medium' },
                    { id: 3, req: 'Apply discount codes at checkout', module: 'Checkout', priority: 'High' },
                  ].map((item) => (
                    <tr key={item.id} className="table-row">
                      <td className="table-cell primary">{item.req}</td>
                      <td className="table-cell secondary">{item.module}</td>
                      <td className="table-cell">
                        <span className={`status-badge ${item.priority === 'High' ? 'failed' : 'warning'}`}>
                          {item.priority}
                        </span>
                      </td>
                      <td className="table-cell">
                        <button
                          className="action-link"
                          onClick={() => handleGenerateTestCases(item, item.module)}
                        >
                          Generate Test Cases
                        </button>
                      </td>
                    </tr>
                  ))}
                  
                  {/* Project 2: CRM System */}
                  {selectedProject?.id === 2 && [
                    { id: 1, req: 'Bulk customer import', module: 'Customer Management', priority: 'High' },
                    { id: 2, req: 'Email campaign tracking', module: 'Lead Tracking', priority: 'Medium' },
                    { id: 3, req: 'Custom report generation', module: 'Reporting', priority: 'High' },
                  ].map((item) => (
                    <tr key={item.id} className="table-row">
                      <td className="table-cell primary">{item.req}</td>
                      <td className="table-cell secondary">{item.module}</td>
                      <td className="table-cell">
                        <span className={`status-badge ${item.priority === 'High' ? 'failed' : 'warning'}`}>
                          {item.priority}
                        </span>
                      </td>
                      <td className="table-cell">
                        <button
                          className="action-link"
                          onClick={() => handleGenerateTestCases(item, item.module)}
                        >
                          Generate Test Cases
                        </button>
                      </td>
                    </tr>
                  ))}
                  
                  {/* Project 3: Banking Portal */}
                  {selectedProject?.id === 3 && [
                    { id: 1, req: 'Two-factor authentication', module: 'Security', priority: 'Critical' },
                    { id: 2, req: 'Scheduled payments', module: 'Bill Payments', priority: 'Medium' },
                  ].map((item) => (
                    <tr key={item.id} className="table-row">
                      <td className="table-cell primary">{item.req}</td>
                      <td className="table-cell secondary">{item.module}</td>
                      <td className="table-cell">
                        <span className={`status-badge ${item.priority === 'Critical' ? 'failed' : 'warning'}`}>
                          {item.priority}
                        </span>
                      </td>
                      <td className="table-cell">
                        <button
                          className="action-link"
                          onClick={() => handleGenerateTestCases(item, item.module)}
                        >
                          Generate Test Cases
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render user story relationships tab content
  const renderRelationshipsTab = () => {
    return (
      <div>
        <div className="section">
          <h3 className="section-title">User Story to Test Case Relationships</h3>
          <div className="content-panel">
            <p className="info-text">
              This view shows the relationships between user stories and their associated test cases.
            </p>
            
            {userStories.map(story => (
              <div key={story.id} className="story-card">
                <div className="story-header">
                  <h4 className="story-title">{story.title}</h4>
                  <p className="story-description">{story.description}</p>
                </div>
                <div className="story-content">
                  <h5 className="related-tests-title">Associated Test Cases:</h5>
                  <div className="related-tests-list">
                    {testCases
                      .filter(tc => story.testCases.includes(tc.id))
                      .map(tc => (
                        <div key={tc.id} className="related-test-item">
                          <div className="related-test-title">{tc.title}</div>
                          <div className="related-test-meta">
                            <span className={`status-badge ${tc.status === 'Passed' ? 'passed' : 'failed'}`}>
                              {tc.status}
                            </span>
                            <span className="priority-label">Priority: {tc.priority}</span>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Main render function
  return (
    <div className="optimization-container">
      <div className="optimization-header">
        <div className="optimization-icon">
          <i className="fas fa-chart-pie"></i>
        </div>
        <h2 className="optimization-title">Test Case Optimization & Coverage Analysis</h2>
      </div>

      <div className="project-selector">
        <label id="project-picker-label">Project:</label>

        {projectsLoading ? (
          <div className="project-select-loading">
            <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />
            <span>Loading projects…</span>
          </div>
        ) : (
          <div className="project-picker" ref={projectPickerRef}>
            <button
              type="button"
              id="project-select"
              className={`project-picker__trigger${projectPickerOpen ? ' is-open' : ''}`}
              aria-labelledby="project-picker-label"
              aria-haspopup="listbox"
              aria-expanded={projectPickerOpen}
              onClick={() => setProjectPickerOpen((o) => !o)}
            >
              <span className="project-picker__value">
                {selectedProject?.project_name || 'Select a project'}
              </span>
              <span className="project-picker__actions">
                {selectedProject && (
                  <span
                    role="button"
                    tabIndex={0}
                    className="project-picker__clear"
                    aria-label="Clear project"
                    onClick={clearSelectedProject}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        clearSelectedProject(e);
                      }
                    }}
                  >
                    <LuX size={14} />
                  </span>
                )}
                <LuChevronDown size={16} />
              </span>
            </button>

            {projectPickerOpen && (
              <div className="project-picker__menu" role="listbox">
                <div className="project-picker__search">
                  <LuSearch size={15} aria-hidden />
                  <input
                    ref={projectSearchRef}
                    type="search"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="Search all projects…"
                    aria-label="Search projects"
                    autoComplete="off"
                  />
                </div>
                <div className="project-picker__meta">
                  {filteredProjects.length} of {projects.length} projects
                  <span className="project-picker__scope">All workspaces</span>
                </div>
                <div className="project-picker__list">
                  {filteredProjects.length === 0 && (
                    <div className="project-picker__empty">No projects match “{projectSearch.trim()}”</div>
                  )}
                  {filteredProjects.map((project) => {
                    const active = selectedProject?.id?.toString() === project.id?.toString();
                    return (
                      <button
                        key={project.id}
                        type="button"
                        role="option"
                        aria-selected={active}
                        className={`project-picker__option${active ? ' is-active' : ''}`}
                        onClick={() => selectProject(project)}
                      >
                        <span className="project-picker__option-name">{project.project_name}</span>
                        {project.description ? (
                          <span className="project-picker__option-desc">{project.description}</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {projectsError && (
          <div className="error-message">
            <span className="error-text">Error loading projects: {projectsError}</span>
            <button type="button" className="project-picker__retry" onClick={handleRefreshProjects}>
              Retry
            </button>
          </div>
        )}
      </div>

      {!selectedProject && (
        <div className="no-project-message">
          <div className="message-icon">
            <i className="fas fa-project-diagram"></i>
          </div>
          <h3>Please select a project</h3>
          <p>Select a project from the dropdown above to view test case optimization and coverage analysis.</p>
        </div>
      )}

      {selectedProject && (
        <>
          <div className="tab-container">
            <button
              className={`tab-button ${activeTab === 'redundancy' ? 'active' : ''}`}
              onClick={() => setActiveTab('redundancy')}
            >
              Redundancy Detection
            </button>
            <button
              className={`tab-button ${activeTab === 'coverage' ? 'active' : ''}`}
              onClick={() => setActiveTab('coverage')}
            >
              Coverage Analysis
            </button>
            <button
              className={`tab-button ${activeTab === 'relationships' ? 'active' : ''}`}
              onClick={() => setActiveTab('relationships')}
            >
              User Story Relationships
            </button>
            <button
              className={`tab-button ${activeTab === 'mapping' ? 'active' : ''}`}
              onClick={() => setActiveTab('mapping')}
            >
              Requirement-to-Test Mapping
            </button>
          </div>

          {activeTab === 'redundancy' && renderRedundancyTab()}
          {activeTab === 'coverage' && renderCoverageTab()}
          {activeTab === 'relationships' && renderRelationshipsTab()}
          
          {activeTab === 'mapping' && (
            <div>
              <div className="section">
                <h3 className="section-title">Requirement-to-Test Mapping</h3>
                <div className="content-panel">
                  <div className="mapping-tabs">
                    <button
                      className={`mapping-tab-button ${mappingView === 'matrix' ? 'active' : ''}`}
                      onClick={() => setMappingView('matrix')}
                    >
                      Traceability Matrix
                    </button>
                    <button
                      className={`mapping-tab-button ${mappingView === 'uncovered' ? 'active' : ''}`}
                      onClick={() => setMappingView('uncovered')}
                    >
                      Uncovered Requirements
                    </button>
                  </div>
                  
                  <div className="traceability-matrix" style={{ display: mappingView === 'matrix' ? 'block' : 'none' }}>
                    <div className="matrix-controls">
                      <div className="matrix-filter">
                        <input
                          type="text"
                          placeholder="Filter test cases..."
                          className="matrix-filter-input"
                          value={filterText}
                          onChange={handleFilterChange}
                        />
                        <button
                          className="matrix-filter-button"
                          onClick={applyFilter}
                        >
                          Filter
                        </button>
                      </div>
                      <div className="matrix-view-options">
                        <label className="view-option">
                          <input
                            type="radio"
                            name="view-mode"
                            checked={viewMode === 'grid'}
                            onChange={() => setViewMode('grid')}
                          />
                          <span>Grid View</span>
                        </label>
                        <label className="view-option">
                          <input
                            type="radio"
                            name="view-mode"
                            checked={viewMode === 'compact'}
                            onChange={() => setViewMode('compact')}
                          />
                          <span>Compact View</span>
                        </label>
                      </div>
                    </div>
                    
                    <div className={`matrix-container ${viewMode === 'compact' ? 'compact-view' : 'grid-view'}`}>
                      <table className="matrix-table">
                        <thead>
                          <tr>
                            <th className="matrix-header-cell requirements-header">Requirements</th>
                            {(filteredTestCases.length > 0 ? filteredTestCases : testCases)
                              .slice(currentPage * testCasesPerPage, (currentPage + 1) * testCasesPerPage)
                              .map(tc => (
                              <th key={tc.id} className="matrix-header-cell tc-header">
                                {tc.id}
                              </th>
                            ))}
                            <th className="matrix-header-cell coverage-header">Coverage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {requirements.map(req => {
                            // Calculate which test cases cover this requirement
                            const coveredByTestCases = testCases
                              .filter(tc => {
                                // Simple logic for demo: test cases with similar words in title cover the requirement
                                const reqWords = req.title.toLowerCase().split(' ');
                                const tcWords = tc.title.toLowerCase().split(' ');
                                return reqWords.some(word => tcWords.includes(word));
                              })
                              .map(tc => tc.id);
                            
                            // Calculate coverage percentage
                            const coveragePercent = testCases.length > 0
                              ? Math.round((coveredByTestCases.length / testCases.length) * 100)
                              : 0;
                            return (
                              <tr key={req.id} className="matrix-row">
                                <td className="matrix-cell requirement-cell">
                                  <div className="requirement-title">{req.title}</div>
                                  <div className="requirement-description">{req.description}</div>
                                </td>
                                
                                {(filteredTestCases.length > 0 ? filteredTestCases : testCases)
                                  .slice(currentPage * testCasesPerPage, (currentPage + 1) * testCasesPerPage)
                                  .map(testCase => {
                                  const isCovered = coveredByTestCases.includes(testCase.id);
                                  
                                  return (
                                    <td key={testCase.id} className="matrix-cell coverage-indicator-cell">
                                      <div className="coverage-cell-content">
                                        <div
                                          className={`coverage-block ${isCovered ? 'covered' : 'not-covered'}`}
                                          title={`${req.title} - ${testCase.title}: ${isCovered ? 'Covered' : 'Not Covered'}`}
                                        ></div>
                                        <div className="coverage-actions">
                                          <button
                                            className="action-icon edit-icon"
                                            title="Edit Test Case"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditTestCase(testCase);
                                            }}
                                          >
                                            ✏️
                                          </button>
                                          <button
                                            className="action-icon ai-icon"
                                            title="AI Suggestions"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleAISuggestions(testCase);
                                            }}
                                          >
                                            🤖
                                          </button>
                                        </div>
                                      </div>
                                    </td>
                                  );
                                  })}
                                
                                <td className="matrix-cell coverage-cell">
                                  <div className="coverage-bar-container">
                                    <div className="coverage-bar">
                                      <div
                                        className="coverage-bar-fill"
                                        style={{ width: `${coveragePercent}%` }}
                                      ></div>
                                    </div>
                                    <span className="coverage-percentage">{coveragePercent}%</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    
                    <Pagination
                      page={currentPage + 1}
                      pageSize={testCasesPerPage}
                      total={(filteredTestCases.length > 0 ? filteredTestCases : testCases).length}
                      onPageChange={(page) => setCurrentPage(Math.max(0, page - 1))}
                      showPageSize={false}
                    />
                    
                    <div className="matrix-legend">
                      <div className="legend-item">
                        <div className="legend-color covered"></div>
                        <span>Covered</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color not-covered"></div>
                        <span>Not Covered</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="uncovered-requirements" style={{ display: mappingView === 'uncovered' ? 'block' : 'none' }}>
                    <h4 className="subsection-title">Uncovered Requirements</h4>
                    <div className="requirements-list">
                      {requirements.filter(req => !req.covered).length > 0 ? (
                        requirements.filter(req => !req.covered).map(req => (
                          <div key={req.id} className="requirement-item">
                            <div className="requirement-header">
                              <h5 className="requirement-title">{req.title}</h5>
                              <button
                                className="table-cell action-link"
                                onClick={() => handleGenerateTestCases({ req: req.title }, req.title)}
                              >
                                Generate Test Cases
                              </button>
                            </div>
                            <p className="requirement-description">{req.description}</p>
                          </div>
                        ))
                      ) : (
                        <div className="empty-state">
                          <p>All requirements are covered by test cases. Great job!</p>
                        </div>
                      )}
                
                      {/* Edit Test Case Modal */}
                      {selectedTestCase && (
                        <div data-rt-modal-overlay className="modal-overlay" style={{ display: isEditModalOpen ? 'flex' : 'none' }} onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
                          <div className="modal-content">
                            <div className="modal-header">
                              <h3>Edit Test Case</h3>
                              <button className="close-button" onClick={() => setIsEditModalOpen(false)}>×</button>
                            </div>
                            <div className="modal-body">
                              <form onSubmit={(e) => {
                                e.preventDefault();
                                const updatedTestCase = {
                                  ...selectedTestCase,
                                  title: e.target.title.value,
                                  steps: e.target.steps.value,
                                  expected: e.target.expected.value,
                                  priority: e.target.priority.value
                                };
                                handleTestCaseUpdate(updatedTestCase);
                              }}>
                                <div className="form-group">
                                  <label htmlFor="title">Title:</label>
                                  <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    defaultValue={selectedTestCase.title}
                                    className="form-control"
                                  />
                                </div>
                                <div className="form-group">
                                  <label htmlFor="steps">Steps:</label>
                                  <textarea
                                    id="steps"
                                    name="steps"
                                    defaultValue={selectedTestCase.steps}
                                    className="form-control"
                                    rows="4"
                                  ></textarea>
                                </div>
                                <div className="form-group">
                                  <label htmlFor="expected">Expected Result:</label>
                                  <textarea
                                    id="expected"
                                    name="expected"
                                    defaultValue={selectedTestCase.expected}
                                    className="form-control"
                                    rows="3"
                                  ></textarea>
                                </div>
                                <div className="form-group">
                                  <label htmlFor="priority">Priority:</label>
                                  <select
                                    id="priority"
                                    name="priority"
                                    defaultValue={selectedTestCase.priority}
                                    className="form-control"
                                  >
                                    <option value="Critical">Critical</option>
                                    <option value="High">High</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Low">Low</option>
                                  </select>
                                </div>
                                <div className="form-actions">
                                  <button type="submit" className="save-button">Save Changes</button>
                                  <button type="button" className="cancel-button" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                                </div>
                              </form>
                            </div>
                          </div>
                        </div>
                      )}
                
                      {/* AI Suggestions Modal */}
                      {selectedTestCase && (
                        <div data-rt-modal-overlay className="modal-overlay" style={{ display: isAISuggestionModalOpen ? 'flex' : 'none' }} onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
                          <div className="modal-content">
                            <div className="modal-header">
                              <h3>AI Suggestions for Test Case Improvement</h3>
                              <button className="close-button" onClick={() => setIsAISuggestionModalOpen(false)}>×</button>
                            </div>
                            <div className="modal-body">
                              <div className="test-case-details">
                                <h4>Current Test Case</h4>
                                <p><strong>Title:</strong> {selectedTestCase.title}</p>
                                <p><strong>Steps:</strong> {selectedTestCase.steps}</p>
                                <p><strong>Expected Result:</strong> {selectedTestCase.expected}</p>
                              </div>
                              
                              <div className="ai-suggestions">
                                <h4>AI Suggestions</h4>
                                <div className="suggestion-category">
                                  <h5>Improve Coverage</h5>
                                  <div className="suggestion-item">
                                    <p>Add validation for edge cases in the input fields.</p>
                                    <button
                                      className="apply-suggestion-button"
                                      onClick={() => handleApplySuggestions("Add validation for edge cases", selectedTestCase.id)}
                                    >
                                      Apply
                                    </button>
                                  </div>
                                </div>
                                
                                <div className="suggestion-category">
                                  <h5>Add Negative Tests</h5>
                                  <div className="suggestion-item">
                                    <p>Test with invalid credentials to verify error handling.</p>
                                    <button
                                      className="apply-suggestion-button"
                                      onClick={() => handleApplySuggestions("Test with invalid credentials", selectedTestCase.id)}
                                    >
                                      Apply
                                    </button>
                                  </div>
                                </div>
                                
                                <div className="suggestion-category">
                                  <h5>Refine Test Steps</h5>
                                  <div className="suggestion-item">
                                    <p>Break down complex steps into smaller, more specific actions.</p>
                                    <button
                                      className="apply-suggestion-button"
                                      onClick={() => handleApplySuggestions("Break down complex steps", selectedTestCase.id)}
                                    >
                                      Apply
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* AI Test Case Generation Modal */}
      {selectedRequirement && (
        <AITestCaseGenerationModal
          isOpen={isAIModalOpen}
          onClose={() => setIsAIModalOpen(false)}
          requirement={selectedRequirement.requirement}
          module={selectedRequirement.module}
          projectId={selectedRequirement.projectId}
          onTestCasesGenerated={handleTestCasesGenerated}
        />
      )}
    </div>
  );
};

export default TestCaseOptimization;